import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { FloppyDisk, Info } from '@phosphor-icons/react';
import {
  PricingConfiguration as PricingConfigType,
  CostEstimate,
  BillingDetails,
} from './types';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';

// Common input styles
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    height: '40px',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderWidth: '1px',
      borderColor: '#DCE0E7',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
    fontSize: '14px',
    color: '#302B3F',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    transform: 'translate(14px, 9px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
};

const selectStyles = {
  height: '40px',
  borderRadius: '6px',
  backgroundColor: '#FFFFFF',
  '& fieldset': {
    borderWidth: '1px',
    borderColor: '#DCE0E7',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    fontSize: '14px',
    color: '#302B3F',
  },
};

interface PricingConfigurationProps {
  pricingConfig: PricingConfigType;
  pricingErrors: Record<string, string>;
  updatePricingConfig: (updates: Partial<PricingConfigType>) => void;
  handlePricingSubmit: (
    onSuccess?: () => void,
    onError?: () => void
  ) => Promise<boolean>;
  setPricingErrors: (errors: Record<string, string>) => void;
  estimatedCallMinutes: number;
  estimatedForwardingMinutes: number;
  setEstimatedCallMinutes: (value: number) => void;
  setEstimatedForwardingMinutes: (value: number) => void;
  costEstimate: CostEstimate;
  saving?: boolean;
  error?: string | null;
  billingDetails: BillingDetails;
}

const billingIncrements = ['6 seconds', '60 seconds'];

const PricingConfiguration = ({
  pricingConfig,
  pricingErrors,
  updatePricingConfig,
  handlePricingSubmit,
  setPricingErrors,
  estimatedCallMinutes,
  estimatedForwardingMinutes,
  setEstimatedCallMinutes,
  setEstimatedForwardingMinutes,
  costEstimate,
  saving: propsSaving,
  error: propsError,
  billingDetails,
}: PricingConfigurationProps) => {
  const { alert, setAlert, showAlert } = useAlert();
  const saving = propsSaving || false;

  const handleChange = (field: keyof PricingConfigType, value: any) => {
    updatePricingConfig({ [field]: value });
    // Clear error for this field
    if (pricingErrors[field]) {
      const newErrors = { ...pricingErrors };
      delete newErrors[field];
      setPricingErrors(newErrors);
    }
  };

  // Helper function to safely parse numeric input
  const handleNumericChange = (
    field: keyof PricingConfigType,
    value: string
  ) => {
    // Allow empty string (user clearing the field) - store as empty for proper display
    if (value === '') {
      handleChange(field, '' as any);
      return;
    }

    // Parse the value
    const numValue = parseFloat(value);

    // Only update if it's a valid positive number
    if (!isNaN(numValue) && numValue >= 0) {
      handleChange(field, numValue);
    }
    // If invalid or negative, don't update (keeps the previous valid value)
  };

  // Check if billing details are complete
  const isBillingDetailsComplete = () => {
    return (
      billingDetails.companyName &&
      billingDetails.email &&
      billingDetails.address.street1 &&
      billingDetails.address.city &&
      billingDetails.address.state &&
      billingDetails.address.zipCode &&
      billingDetails.address.country
    );
  };

  const handleSubmit = async () => {
    // Check if billing details are complete first
    if (!isBillingDetailsComplete()) {
      showAlert(
        'Please fill in all required billing details before saving. Navigate to Billing Details tab to complete the form.',
        'error'
      );
      return;
    }

    const success = await handlePricingSubmit(
      () => {
        showAlert(
          'Billing configuration saved successfully (includes billing details and pricing)',
          'success'
        );
      },
      () => {
        showAlert(propsError || 'Please fix the errors in the form', 'error');
      }
    );

    if (!success && propsError) {
      showAlert(propsError, 'error');
    }
  };

  const currencySymbol = '$';

  return (
    <Box sx={{ p: 3 }}>
      <MessagePopup alert={alert} setAlert={setAlert} />

      {/* Header with Save Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#2B2D3F',
              mb: 0.5,
            }}
          >
            Pricing Configuration
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '14px' }}
          >
            Configure call rates, GST, telephony charges, and call forwarding
            billing. Saving here will save both billing details and pricing
            configuration.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="medium"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FloppyDisk size={16} weight="bold" />
            )
          }
          onClick={handleSubmit}
          disabled={saving || !isBillingDetailsComplete()}
          sx={{
            textTransform: 'none',
            borderRadius: '6px',
            height: '34px',
            bgcolor: '#5A4CE6',
            color: '#FFFFFF',
            boxShadow: 'none',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#4A3CD6',
              boxShadow: 'none',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>

      {/* GST Configuration Section */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          border: '1px solid #DCE0E7',
          borderRadius: '8px',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '14px' }}
        >
          GST Configuration
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#2B2D3F',
            }}
          >
            Enable GST
          </Typography>
          <Switch
            checked={pricingConfig.gstEnabled}
            onChange={e => handleChange('gstEnabled', e.target.checked)}
            color="primary"
            size="small"
            sx={{
              width: 36,
              height: 20,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: '2px',
                transitionDuration: '300ms',
                '&.Mui-checked': {
                  transform: 'translateX(16px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#6366F1',
                    opacity: 1,
                    border: 0,
                  },
                },
                '&.Mui-focusVisible .MuiSwitch-thumb': {
                  color: '#6366F1',
                  border: '6px solid #fff',
                },
              },
              '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 16,
                height: 16,
              },
              '& .MuiSwitch-track': {
                borderRadius: 26 / 2,
                backgroundColor: '#E9E9EA',
                opacity: 1,
              },
            }}
          />
        </Box>

        {pricingConfig.gstEnabled && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'lightGray.text',
                    mb: 1,
                  }}
                >
                  GST Number (GSTIN)
                </Typography>
                <TextField
                  fullWidth
                  placeholder="22AAAAA0000A1Z5"
                  value={pricingConfig.gstNumber}
                  onChange={e => handleChange('gstNumber', e.target.value)}
                  error={!!pricingErrors.gstNumber}
                  helperText={
                    pricingErrors.gstNumber || '15-character alphanumeric code'
                  }
                  sx={inputStyles}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'lightGray.text',
                    mb: 1,
                  }}
                >
                  GST Percentage
                </Typography>
                <TextField
                  fullWidth
                  placeholder="18"
                  type="number"
                  value={pricingConfig.gstPercentage || ''}
                  onChange={e =>
                    handleNumericChange('gstPercentage', e.target.value)
                  }
                  error={!!pricingErrors.gstPercentage}
                  helperText={
                    pricingErrors.gstPercentage ||
                    'Standard GST rate in India is 18%'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={inputStyles}
                />
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Call Charges Section */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          border: '1px solid #DCE0E7',
          borderRadius: '8px',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '14px' }}
        >
          Call Charges
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Per Minute Rate
              </Typography>
              <TextField
                fullWidth
                placeholder="0.05"
                type="number"
                value={pricingConfig.callRate || ''}
                onChange={e => handleNumericChange('callRate', e.target.value)}
                error={!!pricingErrors.callRate}
                helperText={pricingErrors.callRate}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencySymbol}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">/ min</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.001 }}
                sx={inputStyles}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Billing Increment
              </Typography>
              <Select
                value={pricingConfig.billingIncrement}
                onChange={e => handleChange('billingIncrement', e.target.value)}
                sx={selectStyles}
              >
                {billingIncrements.map(increment => (
                  <MenuItem
                    key={increment}
                    value={increment}
                    sx={{ fontSize: '14px' }}
                  >
                    {increment}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Minimum billing unit for calls
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Telephony Infrastructure Charges Section */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          border: '1px solid #DCE0E7',
          borderRadius: '8px',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '14px' }}
        >
          Telephony Infrastructure Charges
        </Typography>

        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            color: 'lightGray.text',
            mb: 2,
          }}
        >
          Billing Mode
        </Typography>

        <RadioGroup
          value={pricingConfig.telephonyMode}
          onChange={e => handleChange('telephonyMode', e.target.value)}
        >
          <Box
            sx={{
              p: 2,
              mb: 2,
              border:
                pricingConfig.telephonyMode === 'bundled'
                  ? '2px solid #5A4CE6'
                  : '1px solid #DCE0E7',
              borderRadius: '8px',
              backgroundColor: '#FFF',
              cursor: 'pointer',
            }}
            onClick={() => handleChange('telephonyMode', 'bundled')}
          >
            <FormControlLabel
              value="bundled"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Bundled with Call Charges
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Include telephony costs in the per-minute rate
                  </Typography>
                </Box>
              }
            />
          </Box>
          <Box
            sx={{
              p: 2,
              mb: 2,
              border:
                pricingConfig.telephonyMode === 'separate'
                  ? '2px solid #5A4CE6'
                  : '1px solid #DCE0E7',
              borderRadius: '8px',
              backgroundColor: '#FFF',
              cursor: 'pointer',
            }}
            onClick={() => handleChange('telephonyMode', 'separate')}
          >
            <FormControlLabel
              value="separate"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Separate Billing
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bill telephony infrastructure separately with monthly fees
                  </Typography>
                </Box>
              }
            />
          </Box>
        </RadioGroup>

        {pricingConfig.telephonyMode === 'separate' && (
          <FormControl fullWidth>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'lightGray.text',
                mb: 1,
              }}
            >
              Separate Telephony Rate (per minute)
            </Typography>
            <TextField
              fullWidth
              placeholder="0.02"
              type="number"
              value={pricingConfig.telephonyRate || ''}
              onChange={e =>
                handleNumericChange('telephonyRate', e.target.value)
              }
              error={!!pricingErrors.telephonyRate}
              helperText={
                pricingErrors.telephonyRate ||
                'Telephony infrastructure charged separately from call charges'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {currencySymbol}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">/ min</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 0.001 }}
              sx={inputStyles}
            />
          </FormControl>
        )}
      </Box>

      {/* Call Forwarding Charges Section */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          border: '1px solid #DCE0E7',
          borderRadius: '8px',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '14px' }}
        >
          Call Forwarding Charges
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#2B2D3F',
            }}
          >
            Enable Call Forwarding Billing
          </Typography>
          <Switch
            checked={pricingConfig.callForwardingEnabled}
            onChange={e =>
              handleChange('callForwardingEnabled', e.target.checked)
            }
            color="primary"
            size="small"
            sx={{
              width: 36,
              height: 20,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: '2px',
                transitionDuration: '300ms',
                '&.Mui-checked': {
                  transform: 'translateX(16px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#6366F1',
                    opacity: 1,
                    border: 0,
                  },
                },
                '&.Mui-focusVisible .MuiSwitch-thumb': {
                  color: '#6366F1',
                  border: '6px solid #fff',
                },
              },
              '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 16,
                height: 16,
              },
              '& .MuiSwitch-track': {
                borderRadius: 26 / 2,
                backgroundColor: '#E9E9EA',
                opacity: 1,
              },
            }}
          />
        </Box>

        {pricingConfig.callForwardingEnabled && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Forwarding Per Minute Rate
              </Typography>
              <TextField
                fullWidth
                placeholder="0.03"
                type="number"
                value={pricingConfig.forwardingRate || ''}
                onChange={e =>
                  handleNumericChange('forwardingRate', e.target.value)
                }
                error={!!pricingErrors.forwardingRate}
                helperText={
                  pricingErrors.forwardingRate ||
                  'Charged in addition to base call rate'
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencySymbol}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">/ min</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.001 }}
                sx={inputStyles}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Description
              </Typography>
              <TextField
                fullWidth
                placeholder="Additional charges for forwarding calls to external numbers"
                multiline
                rows={3}
                value={pricingConfig.forwardingDescription}
                onChange={e =>
                  handleChange('forwardingDescription', e.target.value)
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                  },
                }}
              />
            </FormControl>

            <Alert severity="info" icon={<Info size={20} />}>
              <Typography variant="body2">
                <strong>How forwarding billing works</strong>
              </Typography>
              <Typography variant="body2">
                When a call is forwarded to an external number, both the initial
                call and the forwarding leg are billed separately. The
                forwarding rate is applied only to the time the call is
                connected to the forwarded destination.
              </Typography>
            </Alert>
          </>
        )}
      </Box>

      {/* Cost Estimator Section */}
      <Box
        sx={{
          mb: 4,
          p: 2,
          border: '1px solid #DCE0E7',
          borderRadius: '8px',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '14px' }}
        >
          Cost Estimator
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'lightGray.text',
                  mb: 1,
                }}
              >
                Estimated Call Minutes
              </Typography>
              <TextField
                fullWidth
                placeholder="1000"
                type="number"
                value={estimatedCallMinutes}
                onChange={e =>
                  setEstimatedCallMinutes(parseFloat(e.target.value) || 0)
                }
                sx={inputStyles}
              />
            </FormControl>
          </Grid>
          {pricingConfig.callForwardingEnabled && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'lightGray.text',
                    mb: 1,
                  }}
                >
                  Estimated Forwarding Minutes
                </Typography>
                <TextField
                  fullWidth
                  placeholder="200"
                  type="number"
                  value={estimatedForwardingMinutes}
                  onChange={e =>
                    setEstimatedForwardingMinutes(
                      parseFloat(e.target.value) || 0
                    )
                  }
                  sx={inputStyles}
                />
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box
          sx={{
            backgroundColor: '#FFF',
            p: 3,
            borderRadius: '8px',
            border: '1px solid #DCE0E7',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', color: '#2B2D3F' }}
            >
              {pricingConfig.telephonyMode === 'bundled'
                ? 'Call Charges (incl. Telephony)'
                : 'Base Call Charges'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
            >
              {currencySymbol}
              {costEstimate.baseCallCharges.toFixed(2)}
            </Typography>
          </Box>
          {pricingConfig.telephonyMode === 'separate' &&
            costEstimate.telephonyCharges > 0 && (
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: '14px', color: '#2B2D3F' }}
                >
                  Telephony Infrastructure Charges
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
                >
                  {currencySymbol}
                  {costEstimate.telephonyCharges.toFixed(2)}
                </Typography>
              </Box>
            )}
          {pricingConfig.callForwardingEnabled &&
            costEstimate.callForwarding > 0 && (
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: '14px', color: '#2B2D3F' }}
                >
                  Call Forwarding
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
                >
                  {currencySymbol}
                  {costEstimate.callForwarding.toFixed(2)}
                </Typography>
              </Box>
            )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', color: '#2B2D3F' }}
            >
              Subtotal
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
            >
              {currencySymbol}
              {costEstimate.subtotal.toFixed(2)}
            </Typography>
          </Box>
          {pricingConfig.gstEnabled && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: '14px', color: '#2B2D3F' }}
              >
                GST ({pricingConfig.gstPercentage}%)
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
              >
                {currencySymbol}
                {costEstimate.gst.toFixed(2)}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              pt: 2,
              borderTop: '2px solid #DCE0E7',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: '16px', fontWeight: 600, color: '#2B2D3F' }}
            >
              Total
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: '20px', fontWeight: 600, color: 'primary.main' }}
            >
              {currencySymbol}
              {costEstimate.total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Current Rates Summary */}
      <Box
        sx={{
          p: 2,
          border: '1px solid #DCE0E7',
          borderRadius: '8px',
          bgcolor: '#F8F9FC',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#2B2D3F', mb: 2, fontSize: '14px' }}
        >
          Current Rates Summary
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', color: '#2B2D3F' }}
          >
            Base Call Rate:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
          >
            {currencySymbol}
            {(Number(pricingConfig.callRate) || 0).toFixed(2)}/min
          </Typography>
        </Box>
        {pricingConfig.telephonyMode === 'separate' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', color: '#2B2D3F' }}
            >
              Telephony (Separate):
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
            >
              {currencySymbol}
              {(Number(pricingConfig.telephonyRate) || 0).toFixed(2)}/min
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', fontWeight: 600, color: '#2B2D3F' }}
          >
            {pricingConfig.telephonyMode === 'separate'
              ? 'Total Rate (Call + Telephony):'
              : 'Effective Call Rate:'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', fontWeight: 600, color: 'primary.main' }}
          >
            {currencySymbol}
            {(pricingConfig.telephonyMode === 'separate'
              ? (Number(pricingConfig.callRate) || 0) +
                (Number(pricingConfig.telephonyRate) || 0)
              : Number(pricingConfig.callRate) || 0
            ).toFixed(2)}
            /min
          </Typography>
        </Box>
        {pricingConfig.callForwardingEnabled && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', color: '#2B2D3F' }}
            >
              Call Forwarding:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
            >
              {currencySymbol}
              {(Number(pricingConfig.forwardingRate) || 0).toFixed(2)}/min
            </Typography>
          </Box>
        )}
        {pricingConfig.gstEnabled && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', color: '#2B2D3F' }}
            >
              GST:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', fontWeight: 500, color: '#2B2D3F' }}
            >
              {pricingConfig.gstPercentage}%
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PricingConfiguration;
